import { Instrument } from '@iv/model/instrument';
import {
  IChartPointsResponse,
  ISingleContractAnalyzerChartPoint,
  PointXType
} from 'app/charts-dashboard/interfaces/chart-http.interface';

export interface IChartFilter<T = IChartFilterPattern<Pattern>[]> {
  parameterId: ParameterId;
  name: string;
  type: ChartFilterType;
  pattern: T;
  description: string;
}

export type ChartFilterType = ChartFilterTypeEnum.ENUM | ChartFilterTypeEnum.INT | ChartFilterTypeEnum.TIMESTAMP | ChartFilterTypeEnum.CUSTOM;

export enum ChartFilterTypeEnum {
  ENUM = 'enum',
  TIMESTAMP = 'timestamp',
  INT = 'int',
  CUSTOM = 'custom'
}

export interface IChartFilterPattern<T> {
  description: string;
  name: T;
}

export type Pattern = TimeRange |
  IvxTerm |
  HvTerm |
  OptionType |
  Moneyness |
  Realtime |
  Datetime |
  IvsType |
  boolean |
  TotalOIType |
  DateType |
  Strike |
  SingleContractAnalyzerType;

export type ChartType =
  ChartTypeEnum.TERM_STRUCTURE_ROLLING |
  ChartTypeEnum.IMPLIED_VOLATILITY |
  ChartTypeEnum.HISTORICAL_VOLATILITY |
  ChartTypeEnum.TOTAL_OPEN_INTEREST_VOLUME |
  ChartTypeEnum.IVX_HISTORY |
  ChartTypeEnum.SPOT_CHART |
  ChartTypeEnum.SKEW_CHART |
  ChartTypeEnum.PREMIUM_CHART |
  ChartTypeEnum.IV_DIFFERENCE |
  ChartTypeEnum.SINGLE_CONTRACT_ANALYZER;

export enum ChartTypeEnum {
  TERM_STRUCTURE_ROLLING = 'TERM_STRUCTURE_ROLLING',
  IMPLIED_VOLATILITY = 'IMPLIED_VOLATILITY',
  HISTORICAL_VOLATILITY = 'HISTORICAL_VOLATILITY',
  TOTAL_OPEN_INTEREST_VOLUME = 'TOTAL_OI_VOLUME',
  IVX_HISTORY = 'IVX_HISTORY',
  SPOT_CHART = 'SPOT_CHART',
  SKEW_CHART = 'SKEW_CHART',
  PREMIUM_CHART = 'IV_PREMIUM_CHART',
  IV_DIFFERENCE = 'IV_DIFFERENCE',
  SINGLE_CONTRACT_ANALYZER = 'SINGLE_CONTRACT_ANALYZER'
}

export type ParameterId =
  ChartFilterPatternNameEnum.STOCK_ID |
  ChartFilterPatternNameEnum.DATE_TIME |
  ChartFilterPatternNameEnum.FROM |
  ChartFilterPatternNameEnum.REALTIME |
  ChartFilterPatternNameEnum.IVX_TERM |
  ChartFilterPatternNameEnum.EOD_TIME_RANGE |
  ChartFilterPatternNameEnum.HV_TERM |
  ChartFilterPatternNameEnum.IVS_TYPE |
  ChartFilterPatternNameEnum.MONEYNESS |
  ChartFilterPatternNameEnum.OPTION_TYPE |
  ChartFilterPatternNameEnum.TIME_RANGE |
  CustomControlsNameEnum.V_MODE_TOGGLER |
  CustomControlsNameEnum.TOTAL_OI_CHART_TYPE |
  CustomControlsNameEnum.EXPIRATION_DATE |
  CustomControlsNameEnum.STRIKE |
  CustomControlsNameEnum.SINGLE_CONTRACT_ANALYZER_TYPE;

export enum ChartFilterPatternNameEnum {
  STOCK_ID = 'stockId',
  IVX_TERM = 'ivxTerm',
  HV_TERM = 'hvTerm',
  TIME_RANGE = 'timeRange',
  EOD_TIME_RANGE = 'eodTimeRange',
  OPTION_TYPE = 'optionType',
  MONEYNESS = 'moneyness',
  REALTIME = 'realtime',
  DATE_TIME = 'datetime',
  IVS_TYPE = 'ivsType',
  FROM = 'from',
  SINGLE_CONTRACT_ANALYZER_TYPE = 'singleContractAnalyzerType'
}

export enum SystemControlsNameEnum {
  RELATIVE_MODE = 'relativeMode' // TODO: Вернуть при надобности relative mode
}

export enum CustomControlsNameEnum {
  STOCK_ID_FIRST = 'stockId-first',
  STOCK_ID_SECOND = 'stockId-second',
  IVX_TERM_FIRST = 'ivxTerm-first',
  IVX_TERM_SECOND = 'ivxTerm-second',
  MONEYNESS_FIRST = 'moneyness-first',
  MONEYNESS_SECOND = 'moneyness-second',
  V_MODE_TOGGLER = 'vModeToggler',
  TOTAL_OI_CHART_TYPE = 'totalOiChartType',
  EXPIRATION_DATE = 'expirationDate',
  STRIKE = 'strike',
  SINGLE_CONTRACT_ANALYZER_TYPE = 'singleContractAnalyzerType'
}

export interface ICardChartData {
  chart: ChartType;
  stock: Instrument;
  selectedFilters: ISelectedFilters;
  filters: IChartFilter[];
}

// TODO: Расширить поведение этого интерфейсы. https://jira.egartech.com/browse/IVLIVE-828
export interface ISelectedFilters {
  timeRange?: IChartFilterPattern<TimeRange>;
  eodTimeRange?: IChartFilterPattern<EodTimeRange>;
  hvTerm?: IChartFilterPattern<HvTerm>;
  ivxTerm?: IChartFilterPattern<IvxTerm>;
  optionType?: IChartFilterPattern<OptionType>;
  moneyness?: IChartFilterPattern<Moneyness>;
  realtime?: IChartFilterPattern<Realtime>;
  datetime?: Datetime;
  ivsType?: IChartFilterPattern<IvsType>;
  vModeToggler?: boolean;
  totalOiChartType?: IChartFilterPattern<TotalOIType>;
  'stockId-first'?: Instrument;
  'stockId-second'?: Instrument;
  'moneyness-first'?: IChartFilterPattern<Moneyness>;
  'moneyness-second'?: IChartFilterPattern<Moneyness>;
  'ivxTerm-first'?: IChartFilterPattern<IvxTerm>;
  'ivxTerm-second'?: IChartFilterPattern<IvxTerm>;
  strike?: IChartFilterPattern<Strike>;
  expirationDate?: IChartFilterPattern<DateType>;
  singleContractAnalyzerType?: IChartFilterPattern<SingleContractAnalyzerType>;
}

export type TimeRange =
  TimeRangeEnum.D1 |
  TimeRangeEnum.H24 |
  TimeRangeEnum.D3 |
  TimeRangeEnum.M1 |
  TimeRangeEnum.M3 |
  TimeRangeEnum.M6 |
  TimeRangeEnum.Y1 |
  TimeRangeEnum.Y5 |
  TimeRangeEnum.ALL;
export type EodTimeRange =
  TimeRangeEnum.M1 |
  TimeRangeEnum.M3 |
  TimeRangeEnum.M6 |
  TimeRangeEnum.Y1 |
  TimeRangeEnum.Y5;
export type IvxTerm = '7' | '14' | '21' | '30' | '60' | '90' | '120' | '150' | '180' | '270' | '360' | '720' | '1080' | 'T360';
export type HvTerm = '10' | '20' | '30' | '60' | '90' | '180' | 'T10' | 'T180';
export type OptionType = OptionTypeEnum.CALL | OptionTypeEnum.PUT | OptionTypeEnum.CALL_PUT;
export type Moneyness = '0' | '5' | '10' | '15' | '20' | '25' | '30' | '35' | '40' | '45' | '50' | '55' | '60' | 'M100';
export type Realtime = RealTimeEnum.ON | RealTimeEnum.OFF;
export type Datetime = 'yyyy-MM-dd HH:mm:ss';
export type DateType = 'yyyy-MM-dd';
export type Strike = number;
export type IvsType = IvsTypeEnum.DELTA | IvsTypeEnum.MONEYNESS | IvsTypeEnum.STRIKE;
export type TotalOIType = TotalOIEnum.OPEN_INTEREST | TotalOIEnum.VOLUME;
export type SingleContractAnalyzerType =
  SingleContractAnalyzerTypeEnum.LAST_PRICE | // Пока что только два вида
  SingleContractAnalyzerTypeEnum.IV |
  SingleContractAnalyzerTypeEnum.BID |
  SingleContractAnalyzerTypeEnum.ASK;

export enum RealTimeEnum {
  ON = 'ON',
  OFF = 'OFF'
}

export enum TimeRangeEnum {
  D1 = 'D1',
  H24 = 'H24',
  D3 = 'D3',
  M1 = 'M1',
  M3 = 'M3',
  M6 = 'M6',
  Y1 = 'Y1',
  Y5 = 'Y5',
  ALL = 'ALL'
}

export enum OptionTypeEnum {
  CALL = 'CALL',
  PUT = 'PUT',
  CALL_PUT = 'CALL_PUT'
}

export enum IvsTypeEnum {
  DELTA = 'delta',
  MONEYNESS = 'moneyness',
  STRIKE = 'strike'
}

export enum TotalOIEnum {
  OPEN_INTEREST = 'OI',
  VOLUME = 'Volume'
}

// bid, ask, mean, delta, gamma, rho, theta, vega, bidiv, askiv, impvol, volume, forwardprice, iv, lastprice, bidsize, asksize, openinterest
export enum SingleContractAnalyzerTypeEnum {
  TIME = 'time',
  LAST_PRICE = 'lastprice',
  IV = 'iv',
  BID = 'bid',
  BID_SIZE = 'bidsize',
  BID_IV = 'bidiv',
  ASK = 'ask',
  ASK_SIZE = 'asksize',
  ASK_IV = 'askiv',
  OPT_VOL = 'optvol',
  OPEN_INTEREST = 'openinterest',
  MEAN = 'mean',
  DELTA = 'delta',
  GAMMA = 'gamma',
  RHO = 'rho',
  THETA = 'theta',
  VEGA = 'vega',
  IMPVOL = 'impvol',
  VOLUME = 'volume',
  FORWARD_PRICE = 'forwardprice',
}

export interface IChartSeriesSettings {
  color: string;
}

export interface ILCPoint {
  x: number;
  y: number;
  z?: number;
  additional?: { x: { value: number } };
}

export interface IPointsData {
  [seriesId: string]: IChartPointsResponse<PointXType> | ISingleContractAnalyzerChartPoint[];
}

export interface IHistoricalPointsData {
  [seriesId: string]: IChartPointsResponse<PointXType>;
}
